function formatDateLong(d) {
  if (!d) return '';
  const raw = String(d).slice(0, 10);
  const date = new Date(`${raw}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function cityStateZip(city, state, zip) {
  const parts = [city, state].filter(Boolean);
  const cs = parts.join(', ');
  if (zip) return cs ? `${cs} ${zip}` : String(zip);
  return cs;
}

function formatLbs(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '';
  return `${Math.round(v).toLocaleString('en-US')} lbs`;
}

function dimsInches(l, w, h) {
  const a = Number(l);
  const b = Number(w);
  const c = Number(h);
  if (!(a > 0 && b > 0 && c > 0)) return '';
  const toIn = (ft) => Math.round(ft * 12);
  return `${toIn(a)} x ${toIn(b)} x ${toIn(c)} in`;
}

/**
 * Build printable manifest data from freight detail + form overrides.
 * @param {object} freight - GET /api/freight/:id response
 * @param {object} form - manifest form fields
 * @param {object} [extras] - optional { inventory_items: [] }
 */
export function buildManifestDataFromFreight(freight, form = {}, extras = {}) {
  const inv = extras.inventory_items || freight.inventory_items || [];
  const driverName = [freight.driver_first_name, freight.driver_last_name].filter(Boolean).join(' ').trim();

  const freightLines = inv.map((row, idx) => {
    const qty = Number(row.quantity) || 0;
    const perLb = Number(row.weight) || 0;
    return {
      line: idx + 1,
      handling_unit: qty,
      pieces: qty,
      package_type: 'Pallet',
      description: row.description || `Inventory #${row.inventory_items_id}`,
      nmfc: '—',
      freight_class: '—',
      weight: formatLbs(perLb * qty),
      dimensions: dimsInches(row.length_feet, row.width_feet, row.height_feet),
    };
  });

  const totalWeightLbs =
    freightLines.reduce((sum, line) => {
      const m = String(line.weight || '').replace(/[^\d]/g, '');
      return sum + (Number(m) || 0);
    }, 0) || (Number(freight.weight_tons) > 0 ? Number(freight.weight_tons) * 2000 : 0);

  const totalQty = Number(form.pieces ?? freight.quantity) || 0;
  const shipDate = form.ship_date || freight.pickup_date;

  return {
    manifest_number: form.manifest_number || '',
    shipment_date: formatDateLong(shipDate),
    carrier_name: freight.carrier_name || '',
    carrier_scac: freight.carrier_mc_number || freight.carrier_dot_number || '',
    pro_number: freight.freight_number || '',
    bol_number: freight.freight_number || '',
    trailer_number: freight.truck_number || '',
    seal_number: '',
    driver_name: driverName,
    driver_phone: freight.driver_phone || '',
    pickup_type: 'LTL',
    service_level: form.service_level || 'Standard',
    freight_terms: 'Prepaid',
    payment_terms: 'Third Party Billing',

    shipper_name: freight.shipper_name || '',
    shipper_address: freight.shipper_address || '',
    shipper_city_state_zip: cityStateZip(freight.shipper_city, freight.shipper_state, freight.shipper_zip_code),
    shipper_contact: freight.shipper_contact || '',
    shipper_phone: freight.shipper_phone || '',
    shipper_email: freight.shipper_email || '',

    consignee_name: freight.consignee_name || '',
    consignee_address: form.delivery_address || freight.consignee_address || '',
    consignee_city_state_zip: cityStateZip(
      form.delivery_city || freight.consignee_city,
      form.delivery_state || freight.consignee_state,
      form.delivery_zip_code || freight.consignee_zip_code
    ),
    consignee_contact: freight.consignee_contact || '',
    consignee_phone: freight.consignee_phone || '',
    consignee_email: freight.consignee_email || '',

    bill_to_name: freight.carrier_name || '',
    bill_to_address: freight.carrier_address || '',
    bill_to_city_state_zip: cityStateZip(freight.carrier_city, freight.carrier_state, freight.carrier_zip_code),
    bill_to_account: '',
    bill_to_phone: freight.carrier_phone || '',

    freight_lines: freightLines,
    total_handling_units: totalQty ? `${totalQty} pallet${totalQty === 1 ? '' : 's'}` : '',
    total_pieces: totalQty ? String(totalQty) : '',
    total_weight: formatLbs(totalWeightLbs),
    total_cube: '',
    hazmat: 'No',
    temp_controlled: 'No',
    stackable: 'Yes',
    liftgate_required: 'No',
    inside_delivery: 'No',

    pickup_date: formatDateLong(freight.pickup_date),
    pickup_window: '',
    pickup_location_type: 'Commercial warehouse',
    dock_available: 'Yes',
    pickup_instructions: freight.description || freight.special_instructions || '',

    estimated_delivery_date: formatDateLong(freight.delivery_date),
    delivery_window: form.delivery_window || '',
    delivery_location_type: 'Commercial receiving dock',
    appointment_required: form.appointment_time ? 'Yes' : 'No',
    delivery_appointment_number: '',
    delivery_instructions: freight.consignee_delivery_instructions || '',

    reference_numbers: [
      { type: 'Freight #', number: freight.freight_number || '' },
      { type: 'Customer PO', number: '' },
    ].filter((r) => r.number),

    accessorials: [
      { name: 'Appointment Delivery', required: form.appointment_time ? 'Yes' : 'No' },
      { name: 'Liftgate Delivery', required: 'No' },
    ],

    certification_text:
      'This manifest represents the freight tendered to the carrier for domestic LTL transportation. Freight count, weight, classification, and condition are subject to carrier inspection and confirmation.',
    shipper_signature_name: freight.shipper_contact || '',
    shipper_signature_date: formatDateLong(shipDate),
    driver_signature_name: driverName,
    driver_signature_date: formatDateLong(shipDate),
  };
}
